import './styles.scss';
import React, {forwardRef, useEffect, useImperativeHandle, useRef} from "react";
import {cn, getParentByClass, isMobileDevice} from "../../../utils/utils";
import {useSelector} from "react-redux";
import users from "../../users";

type ScrollModalViewProps = {
    children: React.ReactNode;
    header: React.ReactNode;
}

export type ScrollModalApi = {
    open: () => void;
    close: () => void;
}

const ScrollModalView = forwardRef<ScrollModalApi, ScrollModalViewProps>(({children, header}, ref) => {

    const [isOpen, setIsOpen] = React.useState(false);
    const startY = useRef(0);
    const lastY = useRef(0);
    const endY = useRef(0);
    const isSwipeBottom = useRef(false);
    const scrollModalRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const darkMode = useSelector(users.selectors.isDarkMode)

    const handleClose = () => {
        setIsOpen(false);
        scrollModalRef.current!.style.bottom = '';
    }

    const handleOpen = () => {
        setIsOpen(true);
    }

    useImperativeHandle(ref, () => ({
        open: handleOpen,
        close: handleClose
    }));

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        }
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', handleEscape);
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!scrollModalRef.current) return;

        const handleSwipeDown = (e: TouchEvent) => {
            if (startY.current === 0) return;


            const modalBody = getParentByClass(e.target as HTMLElement, 'scroll-modal__body');
            if (modalBody && modalBody.scrollTop > 0) {
                startY.current = 0;
                scrollModalRef.current!.style.bottom = `0px`;
                return;
            }

            e.stopPropagation();

            scrollModalRef.current!.classList.add('scroll-modal__deanimate-bottom');
            endY.current = Number(e.touches[0].clientY);
            scrollModalRef.current!.style.bottom = `${Math.min(0, 0 - (endY.current - startY.current))}px`;
            isSwipeBottom.current = endY.current > lastY.current || (endY.current === lastY.current && isSwipeBottom.current);
            lastY.current = Number(endY.current);
        };

        const handleTouchStart = (e: TouchEvent) => {

            const modalBody = getParentByClass(e.target as HTMLElement, 'scroll-modal__body');
            if (modalBody && modalBody.scrollTop > 0) return;

            startY.current = Number(e.touches[0].clientY);
            lastY.current = Number(e.touches[0].clientY);
            endY.current = Number(e.touches[0].clientY);
            isSwipeBottom.current = false;
            scrollModalRef.current!.classList.add('scroll-modal__deanimate-bottom');
        }

        const handleTouchEnd = (e: TouchEvent) => {
            if (startY.current === 0) return;
            startY.current = 0;
            scrollModalRef.current!.classList.remove('scroll-modal__deanimate-bottom');
            if (isSwipeBottom.current) {
                handleClose()
            } else {
                scrollModalRef.current!.style.bottom = `0`;
            }
        }

        scrollModalRef.current.addEventListener('touchstart', handleTouchStart, {passive: true});
        scrollModalRef.current.addEventListener('touchend', handleTouchEnd, {passive: true})
        scrollModalRef.current.addEventListener('touchmove', handleSwipeDown, {passive: true});

        return () => {
            scrollModalRef.current?.removeEventListener('touchstart', handleTouchStart);
            scrollModalRef.current?.removeEventListener('touchend', handleTouchEnd);
            scrollModalRef.current?.removeEventListener('touchmove', handleSwipeDown);
        }
    }, [scrollModalRef.current]);
    useEffect(() => {
        if (!bodyRef.current || !headerRef.current || !scrollModalRef.current) return;

        const contentHeight = bodyRef.current.querySelector('.scroll-modal__body__content')?.clientHeight || 0;
        const headerHeight = headerRef.current.clientHeight;

        const marginBottom = isMobileDevice() ? 60 : 0;

        bodyRef.current.style.height = `calc(min(${contentHeight + 5}px, calc(${window.innerHeight * 0.9}px - 22px - ${headerHeight}px - ${marginBottom}px)))`;
        bodyRef.current.style.marginBottom = `${marginBottom}px`;

    }, [children]);


    const handleBodyModalScroll = (e: React.UIEvent<HTMLDivElement>) => {
    }

    return (
        <>
            <div className={"scroll-modal__backdrop " + (!isOpen && "scroll-modal__backdrop-closed")}
                 onClick={handleClose}>
            </div>
            <div
                className={cn("scroll-modal", {"scroll-modal-dark": darkMode}, (!isOpen && "scroll-modal__modal-closed"))}
                onScroll={e => e.stopPropagation()}
                ref={scrollModalRef}>
                <div className="scroll-modal__icon-scroll">
                    <span></span>
                </div>
                <div ref={headerRef} className="scroll-modal__header">
                    {header}
                </div>
                <hr className="scroll-modal__header__separator"/>
                <div ref={bodyRef} onScroll={handleBodyModalScroll} className="scroll-modal__body">
                    <div className="scroll-modal__body__content">
                        {children}
                    </div>
                </div>
            </div>
        </>)
});

export default ScrollModalView;
